import { useState } from "react";
import { useDroppable } from "@dnd-kit/core";
import { Plus, MoreHorizontal, Pencil, Trash2, Smile } from "lucide-react";
import TaskCard from "@/components/TaskCard";
import EmojiPicker, { Theme } from "emoji-picker-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function BoardColumn({ board, tasks, onAddTask, onToggleTask, onEditTask, onDeleteTask, lists, customTags, onRename, onDeleteSection, onEmojiChange }) {
  const { setNodeRef, isOver } = useDroppable({
    id: board.id,
  });
  const [showEmoji, setShowEmoji] = useState(false);

  return (
    <div 
      ref={setNodeRef}
      className={`anydo-board-column flex flex-col transition-all ${isOver ? 'ring-2 ring-blue-500/50' : ''}`}
      data-testid={`board-${board.id}`}
    >
      {/* Column Header */}
      <div className="p-4 border-b border-white/10 flex items-center justify-between relative">
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={() => setShowEmoji(!showEmoji)}
            className="text-lg hover:scale-110 transition-transform flex-shrink-0"
            data-testid={`board-emoji-btn-${board.id}`}
            title="Change emoji"
          >
            {board.emoji || "📋"}
          </button>
          <h3 className="text-sm font-semibold text-white truncate">{board.name}</h3>
        </div>
        {showEmoji && (
          <div className="absolute top-12 left-2 z-50" data-testid={`board-emoji-picker-${board.id}`}>
            <div className="relative">
              <EmojiPicker
                theme={Theme.DARK}
                width={300}
                height={350}
                searchPlaceholder="Search emoji"
                onEmojiClick={(emojiData) => {
                  if (onEmojiChange) onEmojiChange(emojiData.emoji);
                  setShowEmoji(false);
                }}
              />
              <button
                onClick={() => setShowEmoji(false)}
                className="absolute -top-2 -right-2 w-6 h-6 bg-gray-700 hover:bg-gray-600 rounded-full flex items-center justify-center text-white text-xs z-10"
              >
                ✕
              </button>
            </div>
          </div>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="p-1 hover:bg-white/10 rounded transition-colors"
              data-testid={`section-menu-${board.id}`}
            >
              <MoreHorizontal className="w-4 h-4 text-gray-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[#2d3136] border-white/10">
            {onEmojiChange && (
              <DropdownMenuItem onClick={() => setShowEmoji(true)}>
                <Smile className="w-4 h-4 mr-2" />
                Change Emoji
              </DropdownMenuItem>
            )}
            {onRename && (
              <DropdownMenuItem
                onClick={() => {
                  const name = prompt("Rename section:", board.name);
                  if (name) onRename(name);
                }}
              >
                <Pencil className="w-4 h-4 mr-2" />
                Rename
              </DropdownMenuItem>
            )}
            {onDeleteSection && (
              <DropdownMenuItem
                className="text-red-400"
                onClick={onDeleteSection}
                data-testid={`delete-section-${board.id}`}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Section
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Tasks */}
      <div className="overflow-y-auto p-3 space-y-2">
        {tasks.map((task) => (
          <TaskCard
            key={task.task_id}
            task={task}
            lists={lists}
            customTags={customTags}
            onToggle={onToggleTask}
            onEdit={onEditTask}
            onDelete={onDeleteTask}
          />
        ))}
      </div>

      {/* Add Task Button */}
      <button
        onClick={onAddTask}
        className="flex items-center gap-2 p-4 text-gray-400 hover:text-white transition-colors border-t border-white/10"
        data-testid={`add-task-${board.id}`}
      >
        <Plus className="w-4 h-4" />
        <span className="text-sm">Add Task</span>
      </button>
    </div>
  );
}
